import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileUp, Trash2, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

export const LapImport = ({ data, onImported, onRemove, prefix = "lap" }) => {
  const { t } = useT();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const content = await file.text();
      const { data: parsed } = await api.post("/laps/parse", { filename: file.name, content });
      onImported(parsed);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`${prefix}-import`} className="flex flex-wrap items-center gap-3 bg-[#090A0F] border border-dashed border-[#282D3D] rounded-sm p-4">
      <input ref={inputRef} type="file" accept=".csv,.CSV,text/csv,text/plain" className="hidden" data-testid={`${prefix}-file-input`} onChange={onFile} />
      {data ? (
        <div className="flex items-center gap-2 font-mono text-sm min-w-0">
          <FileSpreadsheet className="w-4 h-4 text-[#10B981] shrink-0" />
          <span data-testid={`${prefix}-file-name`} className="truncate">{data.filename || "CSV"}</span>
          <span className="text-slate-500 text-xs">· {data.laps.length} {t("laps_short")}</span>
        </div>
      ) : (
        <p className="text-sm text-slate-400">{t("laps_import_hint")}</p>
      )}
      <div className="ml-auto flex gap-2">
        <Button type="button" disabled={busy} data-testid={`${prefix}-import-btn`} onClick={() => inputRef.current?.click()}
          className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base h-10">
          <FileUp className="w-4 h-4 mr-1.5" /> {data ? t("replace_csv") : t("import_csv")}
        </Button>
        {data && (
          <Button type="button" variant="outline" data-testid={`${prefix}-remove-btn`} onClick={onRemove}
            className="rounded-sm bg-transparent border-[#282D3D] text-slate-400 hover:text-[#FF3B30] hover:border-[#FF3B30] hover:bg-transparent h-10">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
